import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import {
  getWithDigest,
  setWithDigest,
  getQueryStringParams,
  setQueryStringParam,
  deleteQueryStringParam,
} from "../../shared/utils";

// Constants for member status priority during sorting
const MEMBER_STATUS = {
  EXCLUDED: 0,
  NOT_RESPONDED: 1,
  RESPONDED: 2,
};

// Helper to get member status for sorting
const getMemberStatus = (member) => {
  if (member.exclude) {
    return MEMBER_STATUS.EXCLUDED;
  }
  if (member.responded) {
    return MEMBER_STATUS.RESPONDED;
  }
  return MEMBER_STATUS.NOT_RESPONDED;
};

// Team sort comparators - each returns a number for sorting
const TEAM_SORT_COMPARATORS = {
  snapshotSquadName: (a, b) =>
    a.snapshot_squad_name.localeCompare(b.snapshot_squad_name),

  count: (a, b) => {
    const aPercentage = a.team_size > 0 ? (a.count || 0) / a.team_size : 0;
    const bPercentage = b.team_size > 0 ? (b.count || 0) / b.team_size : 0;
    return aPercentage - bPercentage;
  },

  teamLead: (a, b) => {
    const aManager = a.manager?.name || "";
    const bManager = b.manager?.name || "";
    return aManager.localeCompare(bManager);
  },
};

// Member sort comparators
const MEMBER_SORT_COMPARATORS = {
  count: (a, b) => {
    // Sort by response status with priority order
    const aStatus = getMemberStatus(a);
    const bStatus = getMemberStatus(b);
    const result = aStatus - bStatus;

    // If same status, sort by name as tiebreaker
    if (result === 0) {
      return (a.name || "").localeCompare(b.name || "");
    }
    return result;
  },

  default: (a, b) => (a.name || "").localeCompare(b.name || ""),
};

export function snapshotStatusV2(defaults) {
  const {
    baseUrl,
    snapshotId,
    statsAll = { completed: 0, total: 0, percentage: 0 },
    statsUser = { completed: 0, total: 0, percentage: 0 },
    minTenureWeeks = null,
    goToResultsReason = null,
  } = defaults;

  return {
    // Integrate standard tree expansion mixin
    ...teamTreeExpansionMixin(),

    // ============================================================================
    // STATE
    // ============================================================================

    // Configure ID mapping for status rows and cached squads
    rowIdKey: "tree_parent_id",
    cachedParentKey: "parent_id",

    // Table data state
    isFetchingTableData: false,
    drilldownData: {},
    allExcludedUsers: [], // All excluded users
    yourTeamsExcludedUsers: [], // Excluded users from user's teams only
    excludedModalOpen: false, // Modal open/closed state
    excludedModalSearchTerm: "", // Search filter for excluded users modal

    // Squad hierarchy cache state
    cachedSquadsData: null, // Store squads data directly in component

    // Search state
    teamSearchTerm: "",
    matchedRowIds: new Set(),
    searchExpandedRows: new Set(), // Track rows expanded by search for cleanup

    // Tab state (server-side filtering)
    activeTab: "all-teams", // "your-teams" or "all-teams"

    // Summary stats for both tabs (passed from server)
    statsAll, // { completed, total, percentage } for "All teams"
    statsUser, // { completed, total, percentage } for "Your teams"

    // ============================================================================
    // COMPUTED PROPERTIES
    // ============================================================================

    // Get current aggregation mode from store
    get aggregation() {
      return this.$store?.snapshotResultFilters?.aggregation || "team";
    },

    // Helper to get data for the active tab
    getDataForTab(allTeamsData, yourTeamsData) {
      return this.activeTab === "your-teams" ? yourTeamsData : allTeamsData;
    },

    // Get excluded users for current tab
    get excludedUsers() {
      return this.activeTab === "your-teams"
        ? this.yourTeamsExcludedUsers
        : this.allExcludedUsers;
    },

    // Get stats for current tab
    get currentStats() {
      return this.activeTab === "your-teams" ? this.statsUser : this.statsAll;
    },

    // Get count of excluded users from array
    get excludedCount() {
      return this.excludedUsers.length;
    },

    // Get filtered excluded users based on search
    get filteredExcludedUsers() {
      const searchTerm = this.excludedModalSearchTerm.toLowerCase().trim();

      if (searchTerm.length < 2) {
        return this.excludedUsers;
      }

      return this.excludedUsers.filter((user) => {
        // Search by name only
        return user.name?.toLowerCase().includes(searchTerm);
      });
    },

    // Get current sort column from store
    get drilldownSortBy() {
      return (
        this.$store?.snapshotResultFilters?.drilldownSortBy ||
        "snapshotSquadName"
      );
    },

    // Get current sort direction from store
    get drilldownSortReverse() {
      return this.$store?.snapshotResultFilters?.drilldownSortReverse || false;
    },

    // ============================================================================
    // LIFECYCLE
    // ============================================================================

    init() {
      // Set tab from URL param
      const viewParam = getQueryStringParams().get("view");
      if (viewParam === "your-teams") {
        this.activeTab = "your-teams";
      }

      // First, ensure squad hierarchy is loaded (required for breadcrumbs)
      this.initializeSquadCache();

      // Fetch all team and member data from server
      this.fetchTeamBreakdown();

      // Watch for aggregation changes and refetch data
      this.$watch("$store.snapshotResultFilters.aggregation", () => {
        this.clearTeamSearch();
        this.refetchTeamBreakdown();
      });
    },

    // ============================================================================
    // TREE BUILDING
    // ============================================================================

    /*
     * Build hierarchical tree with teams and members
     * Server returns flat array - we build hierarchy based on parent_id and expansion state
     * Applies search filtering and sorting
     */
    get breakdownItems() {
      const rawData = this.drilldownData?.team_breakdown?.data || [];
      const userData = this.drilldownData?.team_breakdown?.user_data || [];

      // Switch dataset based on active tab (server pre-filters "your teams")
      const dataToUse = this.getDataForTab(rawData, userData);

      // Server returns flat array with all teams and members
      const teams = dataToUse.filter((item) => item.row_type === "team");
      const members = dataToUse.filter((item) => item.row_type === "member");

      // Build team -> members map (used by both aggregation types)
      const membersByTeam = new Map();
      members.forEach((member) => {
        const teamId = member.tree_parent_id;
        if (!membersByTeam.has(teamId)) {
          membersByTeam.set(teamId, []);
        }
        membersByTeam.get(teamId).push(member);
      });

      // For leaf_teams aggregation, show all teams as a flat list (no hierarchy)
      const aggregation = this.$store?.snapshotResultFilters?.aggregation;
      if (aggregation === "leaf_teams") {
        const result = [];

        // Filter and sort teams
        const teamsToShow = this.filterItemsForSearch(teams);
        const sortedTeams = this.sortTeams([...teamsToShow]);

        // Add each team with its members (if expanded)
        sortedTeams.forEach((team) => {
          result.push({ ...team, _depth: 0 });

          const teamId = this.getRowId(team);
          if (this.expandedRows.has(teamId)) {
            const teamMembers = membersByTeam.get(teamId) || [];
            const membersToShow = this.filterItemsForSearch(teamMembers, false);
            const sortedMembers = this.sortMembers([...membersToShow]);

            sortedMembers.forEach((member) => {
              result.push({ ...member, _depth: 1 });
            });
          }
        });

        return result;
      }

      // For hierarchical aggregations, build a tree structure
      // Build parent → children map for efficient lookups
      const childTeamsByParent = new Map();
      teams.forEach((team) => {
        const parentId = team.parent_id;
        if (parentId) {
          if (!childTeamsByParent.has(parentId)) {
            childTeamsByParent.set(parentId, []);
          }
          childTeamsByParent.get(parentId).push(team);
        }
      });

      // Build tree starting from root teams
      const result = [];
      const rootTeams = teams.filter((t) => !t.parent_id);

      const addTeamRecursive = (team, depth = 0) => {
        // Add team with depth for visual indentation
        result.push({ ...team, _depth: depth });

        // Add child teams and members if this team is expanded
        const teamId = this.getRowId(team);
        if (this.expandedRows.has(teamId)) {
          // Add child teams recursively if expanded
          const childTeams = childTeamsByParent.get(teamId) || [];

          // Filter child teams during search - only show matching teams or teams in search path
          const childTeamsToShow = this.filterItemsForSearch(childTeams);

          const sortedChildren = this.sortTeams([...childTeamsToShow]);
          sortedChildren.forEach((childTeam) => {
            addTeamRecursive(childTeam, depth + 1);
          });

          // Add members after child teams
          const teamMembers = membersByTeam.get(teamId) || [];

          // Filter members during search - only show matching members
          const membersToShow = this.filterItemsForSearch(teamMembers, false);

          const sortedMembers = this.sortMembers([...membersToShow]);
          sortedMembers.forEach((member) => {
            result.push({ ...member, _depth: depth + 1 });
          });
        }
      };

      // Process all root teams (filter during search to show only relevant branches)
      const rootTeamsToShow = this.filterItemsForSearch(rootTeams);
      const sortedRoots = this.sortTeams([...rootTeamsToShow]);
      sortedRoots.forEach((team) => addTeamRecursive(team));

      return result;
    },

    // Get all teams (server loads all data upfront)
    getAllTeams() {
      const rawData = this.drilldownData?.team_breakdown?.data || [];
      return rawData.filter((item) => item.row_type === "team");
    },

    // Get all members (server loads all data upfront)
    getAllMembers() {
      const rawData = this.drilldownData?.team_breakdown?.data || [];
      return rawData.filter((item) => item.row_type === "member");
    },

    // ============================================================================
    // TAB FILTERING
    // ============================================================================

    // Switch between "your-teams" and "all-teams" tabs
    switchTab(newTab) {
      this.clearTeamSearch();
      this.activeTab = newTab;
      if (this.$store?.snapshotResultFilters) {
        this.$store.snapshotResultFilters.activeTab = newTab;
      }

      // Update URL param for bookmarking
      if (newTab === "your-teams") {
        setQueryStringParam("view", "your-teams");
      } else {
        deleteQueryStringParam("view");
      }

      // Collapse all rows when switching tabs
      this.expandedRows.clear();
    },

    // ============================================================================
    // SORTING
    // ============================================================================

    /*
     * Sort teams by current sort configuration
     * @param {Array} teams - Teams to sort
     * @returns {Array} Sorted teams
     */
    sortTeams(teams) {
      const sortBy = this.drilldownSortBy;
      const reverse = this.drilldownSortReverse;

      // Get the appropriate comparator, defaulting to name sort
      const comparator =
        TEAM_SORT_COMPARATORS[sortBy] ||
        TEAM_SORT_COMPARATORS.snapshotSquadName;

      return teams.sort((a, b) => {
        const result = comparator(a, b);
        return reverse ? -result : result;
      });
    },

    /**
     * Sort members with status priority (excluded < not responded < responded)
     * @param {Array} members - Members to sort
     * @returns {Array} Sorted members
     */
    sortMembers(members) {
      const sortBy = this.drilldownSortBy;
      const reverse = this.drilldownSortReverse;

      // Get the appropriate comparator - use 'count' comparator for count sorting,
      // otherwise default to alphabetical by name
      const comparator =
        sortBy === "count"
          ? MEMBER_SORT_COMPARATORS.count
          : MEMBER_SORT_COMPARATORS.default;

      return members.sort((a, b) => {
        const result = comparator(a, b);

        // Apply reverse for team name and team lead sorting, and for count sorting
        if (
          reverse &&
          (sortBy === "count" ||
            sortBy === "snapshotSquadName" ||
            sortBy === "teamLead")
        ) {
          return -result;
        }
        return result;
      });
    },

    // Change sort column (toggles reverse if same column)
    changeDrilldownSortBy(sortBy) {
      if (this.drilldownSortBy === sortBy) {
        this.$store.snapshotResultFilters.drilldownSortReverse =
          !this.drilldownSortReverse;
      } else {
        this.$store.snapshotResultFilters.drilldownSortBy = sortBy;
        this.$store.snapshotResultFilters.drilldownSortReverse = false;
      }
    },

    // Check if currently sorted by given column
    isDrilldownSortOrder(col) {
      return this.drilldownSortBy === col;
    },

    // ============================================================================
    // SEARCH & FILTERING
    // ============================================================================

    // Search teams and members with highlighting and auto-expansion
    async performTeamSearch() {
      const searchTerm = this.teamSearchTerm.trim();

      if (searchTerm.length < 3) {
        this.clearSearchResults();
        return;
      }

      const searchLower = searchTerm.toLowerCase();
      const matchedIds = new Set();

      // Search all teams
      this.getAllTeams().forEach((team) => {
        if (team.snapshot_squad_name?.toLowerCase().includes(searchLower)) {
          matchedIds.add(this.getRowId(team));
        }
      });

      // Search all members
      this.getAllMembers().forEach((member) => {
        if (member.name?.toLowerCase().includes(searchLower)) {
          matchedIds.add(this.getRowId(member));
          // Also mark the team as having a match
          if (member.tree_parent_id) {
            matchedIds.add(member.tree_parent_id);
          }
        }
      });

      this.matchedRowIds = matchedIds;
      this.collapseOldSearchResults();
      this.expandMatchedRows();
    },

    /**
     * Filter items to show only search matches or items in ancestor path
     * @param {Array} items - Items to filter
     * @param {Boolean} includeAncestorPath - Include teams expanded to show search path (true for teams, false for members)
     */
    filterItemsForSearch(items, includeAncestorPath = true) {
      if (!this.hasActiveSearch()) {
        return items;
      }

      return items.filter((item) => {
        const itemId = this.getRowId(item);
        return (
          this.matchedRowIds.has(itemId) ||
          (includeAncestorPath && this.searchExpandedRows.has(itemId))
        );
      });
    },

    // Check if search is active (3+ characters)
    hasActiveSearch() {
      return this.teamSearchTerm.trim().length >= 3;
    },

    // Check if a row matches the current search
    isSearchMatch(row) {
      return this.matchedRowIds.has(this.getRowId(row));
    },

    // Note: highlightSearchMatch() is now provided by teamTreeExpansionMixin

    // Expand all ancestors up to root for matched items
    expandMatchedRows() {
      this.getAllTeams().forEach((team) => {
        if (this.matchedRowIds.has(this.getRowId(team))) {
          this.expandAncestorPath(team.tree_parent_id);
        }
      });

      this.getAllMembers().forEach((member) => {
        if (this.matchedRowIds.has(this.getRowId(member))) {
          this.expandAncestorPath(member.tree_parent_id);
        }
      });
    },

    // Walk up team hierarchy to root, tracking ancestors expanded for collapse on search clear
    expandAncestorPath(teamId) {
      let currentId = teamId;
      const cachedSquads = this.getCachedSquads();

      while (currentId) {
        const wasExpanded = this.expandedRows.has(currentId);
        this.expandedRows.add(currentId);

        // Only track if we're expanding it (not if already expanded)
        if (!wasExpanded) {
          this.searchExpandedRows.add(currentId);
        }

        // Use cached squads to find parent (works even if data not loaded yet)
        const squad = cachedSquads.find((s) => s.id === currentId);
        currentId = squad?.parent_id;
      }
    },

    // Collapse rows that were expanded by previous search
    collapseOldSearchResults() {
      this.searchExpandedRows.forEach((rowId) => {
        this.expandedRows.delete(rowId);
      });
      this.searchExpandedRows.clear();
    },

    // Clear search results only (keep input value)
    clearSearchResults() {
      this.matchedRowIds = new Set();
      this.collapseOldSearchResults();
    },

    // Clear search input and results (called by X button)
    clearTeamSearch() {
      this.teamSearchTerm = "";
      this.clearSearchResults();
    },

    // ============================================================================
    // DATA FETCHING
    // ============================================================================

    // Initialize squad hierarchy cache (required for search expansion)
    async initializeSquadCache() {
      const { squadsDigests, currentUser } = window;
      const digestKey = snapshotId;
      const digest = squadsDigests[digestKey];
      const cacheKey = `squads_${currentUser.account_id}_${digestKey}_v2`;

      // Try to load from cache first
      const cachedSquads = getWithDigest(cacheKey, digest);
      if (cachedSquads) {
        this.cachedSquadsData = cachedSquads;
        return;
      }

      // Cache miss - fetch from server
      try {
        const params = new URLSearchParams({ snapshot_id: snapshotId });
        const response = await fetch(`/squads/tree?${params}`);
        const data = await response.json();

        if (!data.error) {
          // Store in localStorage for future page loads
          setWithDigest(cacheKey, data.squads, data.digest);
          // Update window.squadsDigests so future reads will work
          window.squadsDigests[digestKey] = data.digest;
          // Store in component state for immediate use
          this.cachedSquadsData = data.squads;
        } else {
          console.error("Failed to load squad cache:", data.error);
        }
      } catch (error) {
        console.error("Failed to fetch squad cache:", error);
      }
    },

    // Fetch team breakdown data from server
    async fetchTeamBreakdown() {
      this.isFetchingTableData = true;

      const params = new URLSearchParams({
        aggregation: this.aggregation,
      });

      try {
        const response = await fetch(`${baseUrl}/team_breakdown?${params}`);
        const responseData = await response.json();

        // Store the full response with team_breakdown containing data, user_data, and excluded_users
        this.drilldownData = responseData;
        const excludedData = responseData.team_breakdown?.excluded_users || {};
        this.allExcludedUsers = excludedData.data || [];
        this.yourTeamsExcludedUsers = excludedData.user_data || [];

        // Extract summary stats from response
        const summary = responseData.team_breakdown?.summary || {};
        if (summary.all_teams) {
          this.statsAll = {
            completed: summary.all_teams.completed || 0,
            total: summary.all_teams.total || 0,
            percentage:
              summary.all_teams.total > 0
                ? Math.round(
                    (summary.all_teams.completed / summary.all_teams.total) *
                      100,
                  )
                : 0,
          };
        }
        if (summary.your_teams) {
          this.statsUser = {
            completed: summary.your_teams.completed || 0,
            total: summary.your_teams.total || 0,
            percentage:
              summary.your_teams.total > 0
                ? Math.round(
                    (summary.your_teams.completed / summary.your_teams.total) *
                      100,
                  )
                : 0,
          };
        }
      } catch (error) {
        console.error("Error fetching team breakdown:", error);
      } finally {
        this.isFetchingTableData = false;
      }
    },

    // Refetch team breakdown (reset tree state and search first)
    refetchTeamBreakdown() {
      this.clearTeamSearch();
      this.resetTreeState();
      this.fetchTeamBreakdown();
    },

    // Get cached squads from component state
    getCachedSquads() {
      return this.cachedSquadsData || [];
    },

    // Check if a tree_parent_id belongs to user's team tree
    isUserTeam(treeParentId) {
      const userData = this.drilldownData?.team_breakdown?.user_data || [];
      return userData.some((row) => row.tree_parent_id === treeParentId);
    },

    /**
     * Override mixin's handleRowExpand since we load all data upfront (no lazy loading needed)
     */
    handleRowExpand(row) {
      const rowId = this.getRowId(row);
      if (this.expandedRows.has(rowId)) {
        this.expandedRows.delete(rowId);
      } else {
        this.expandedRows.add(rowId);
      }
    },

    // Not used (all data loaded upfront), but required by mixin's validateRequiredMethods
    getChildDataEndpoint() {
      return "";
    },
    getChildDataParams() {
      return {};
    },

    // ============================================================================
    // UTILITIES
    // ============================================================================

    /**
     * Get unique ID for a row (team or member)
     * Teams use tree_parent_id, members use snapshot_request_id
     */
    getRowId(row) {
      if (row.row_type === "member") {
        return `member-${row.snapshot_request_id}`;
      }
      // For teams, use tree_parent_id
      // For attribute tags, use squad_id (which stores tag.id)
      return row[this.rowIdKey] || row.squad_id;
    },

    // Calculate completion percentage for team
    completionPct(teamBreakdown) {
      if (!teamBreakdown.team_size || teamBreakdown.team_size === 0) return 0;
      return (100.0 * teamBreakdown.count) / teamBreakdown.team_size;
    },

    // Format completion percentage for display
    completionPctDisplay(teamBreakdown) {
      const pct = this.completionPct(teamBreakdown);
      return ` (${Math.round(pct)}%)`;
    },

    decorateExclusionReason(contributor) {
      if (!contributor?.exclude) {
        return "";
      }

      let reason;
      if (
        contributor.exclusion_reason?.startsWith(
          "User does not meet minimum tenure",
        )
      ) {
        reason = minTenureWeeks
          ? `Below minimum tenure of ${minTenureWeeks} weeks`
          : "Below minimum tenure";
      } else if (contributor.exclusion_reason === goToResultsReason) {
        reason = "User excluded themselves";
      } else if (
        !contributor.exclusion_reason?.length &&
        !!contributor.excluded_by
      ) {
        reason = `Excluded by ${
          contributor.excluded_by_name || contributor.excluded_by_email
        }`;
      } else if (contributor.exclusion_reason === "User is OOO in Slack") {
        reason = "OOO in Slack";
      }

      return reason || "";
    },

    /**
     * Exclude a member from the snapshot
     * @param {Object} member - Member row data with snapshot_request_id
     */
    async excludeMember(member) {
      if (!member.snapshot_request_id) return;

      const previousState = member.exclude;

      try {
        const response = await fetch("/snapshot_requests/toggle_exclude", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector('[name="csrf-token"]')?.content || "",
          },
          body: JSON.stringify({
            snapshot_request_id: member.snapshot_request_id,
            snapshot_request: { exclude: true },
          }),
        });

        if (!response.ok) throw new Error("Failed to exclude member");

        // Update the member's exclude status in the data
        member.exclude = true;

        // Add to excluded users arrays (both all and user-teams)
        const newExcludedUser = {
          id: member.id,
          name: member.name,
          email: member.email,
          tree_parent_id: member.tree_parent_id,
          exclusion_reason: member.exclusion_reason,
          snapshot_request_id: member.snapshot_request_id,
        };

        this.allExcludedUsers.push(newExcludedUser);
        if (this.isUserTeam(member.tree_parent_id)) {
          this.yourTeamsExcludedUsers.push(newExcludedUser);
        }

        // Show success notification
        window.pushNotification?.({
          title: "User excluded",
          desc: `${member.name} has been excluded from this snapshot.`,
        });

        // Refresh the table to update counts (preserves expanded state)
        this.fetchTeamBreakdown();
      } catch (error) {
        console.error("Error excluding member:", error);
        // Revert optimistic update
        member.exclude = previousState;
        window.pushNotification?.({
          title: "Error",
          desc: "Failed to exclude user. Please try again.",
          type: "error",
        });
      }
    },

    /**
     * Include a member in the snapshot
     * @param {Object} member - Member row data with snapshot_request_id
     */
    async includeMember(member) {
      if (!member.snapshot_request_id) return;

      const previousState = member.exclude;

      try {
        const response = await fetch("/snapshot_requests/toggle_exclude", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector('[name="csrf-token"]')?.content || "",
          },
          body: JSON.stringify({
            snapshot_request_id: member.snapshot_request_id,
            snapshot_request: { exclude: false },
          }),
        });

        if (!response.ok) throw new Error("Failed to include member");

        // Update the member's exclude status in the data
        member.exclude = false;

        // Remove from both excluded users arrays
        this.allExcludedUsers = this.allExcludedUsers.filter(
          (u) => u.snapshot_request_id !== member.snapshot_request_id,
        );
        this.yourTeamsExcludedUsers = this.yourTeamsExcludedUsers.filter(
          (u) => u.snapshot_request_id !== member.snapshot_request_id,
        );

        // Show success notification
        window.pushNotification?.({
          title: "User included",
          desc: `${member.name} has been included in this snapshot.`,
        });

        // Refresh the table to update counts (preserves expanded state)
        this.fetchTeamBreakdown();
      } catch (error) {
        console.error("Error including member:", error);
        // Revert optimistic update
        member.exclude = previousState;
        window.pushNotification?.({
          title: "Error",
          desc: "Failed to include user. Please try again.",
          type: "error",
        });
      }
    },

    /**
     * Include a member from the excluded modal
     * Wrapper to convert excluded user format to member format
     * @param {Object} excludedUser - User from excludedUsers array
     */
    async includeMemberFromModal(excludedUser) {
      // Convert excluded user format to member format
      const member = {
        id: excludedUser.id,
        name: excludedUser.name,
        email: excludedUser.email,
        tree_parent_id: excludedUser.tree_parent_id,
        snapshot_request_id: excludedUser.snapshot_request_id,
        exclude: true,
      };

      // Call existing includeMember method
      await this.includeMember(member);

      // Modal stays open for batch operations
      // User disappears from list automatically (excludedUsers array updated)
    },
  };
}
